import {useLocation, useNavigate, useParams} from 'react-router-dom';

import styles from './walletTransaction.module.scss';
import TransactionSummaryCards from './TransactionSummaryCards';
import TableTopScrollbar from 'components/tableTopScrollbar';
// The page chrome is the swap transactions screen's, reused rather than
// re-declared — same header, toolbar and table shell, so the two wallet
// queues read as one product. Copy it out if they ever need to diverge.
import shell from '../view/swap/swapTransactions.module.scss';
import {Dropdown, Form, Pagination, Table, Tag, Tooltip} from 'antd';
import {useQuery} from '@apollo/client';
import {GET_ALL_WALLET_TRANSACTIONS_FOR_ADMIN} from 'graphql/queries/wallet';
import moment from 'moment';
import {useEffect, useState} from 'react';
import {
  ArrowLeftOutlined,
  FilterOutlined,
  ReloadOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import {Button as AntButton, Empty} from 'antd';
import {useHistoryContextStore} from '../../../zustand/historyContextStore';
import {useIsSuperAdmin} from 'hooks';

import {currencySymbols} from 'utils/mock';
import DBModal from 'components/modal/modal';
import WireTransferDetails from './transferDetails/WireTransferDetails';
import {useSelector} from 'react-redux';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {toast} from 'react-toastify';
import ManualTxModal from 'pages/adminUser/userDetails/tabs/UserWalletSettings/ManualTxModal';
import EmailTriggerModal from './triggerModal/EmailTriggerModal';
import UpdateWalletTransaction from './UpdateWalletTransaction/UpdateWalletTransaction';
import {renderTransactionId} from 'utils/helper';
import {TransactionsFilter} from './TransactionsFilter';
import moreIcon from 'icons/dots.svg';
import {AutomateTransferModal} from './AutomateTransferModal';
import {BulkUploadWithdrawals} from './BulkUploadWithdrawals';
import {BulkAutomateTransfer} from './BulkAutomateTransfer';
import {useLazyQuery} from '@apollo/client';
import ViewFilesModal from 'components/viewFilesModal';
import {
  buildSendNotificationMenuItem,
  useSendUserNotification,
} from 'components/sendUserNotification';
import {
  downloadProviderSafeTransactionReceipt,
  downloadTransactionReceipt,
  supportsProviderSafeReceipt,
} from 'utils/transactionReceipt';
import {WalletTxTypeCell} from 'components/walletTxType';
import {ProviderStatusTag} from './ProviderStatusTag';

// Flat, human-readable summary of the applied wallet-transaction filters — used
// to show what a shortcut captured and to gate whether any filter is active.
const summarizeWalletTxFilters = (vals = {}) => {
  const fmtDate = d => {
    const m = moment(d);
    return m.isValid() ? m.format('DD MMM YYYY') : String(d);
  };
  const chips = [];
  if (vals.search)
    chips.push({key: 'search', label: 'Search', value: vals.search});
  if (vals.type) chips.push({key: 'type', label: 'Type', value: vals.type});
  if (vals.status)
    chips.push({key: 'status', label: 'Status', value: vals.status});
  if (vals.providerStatus) {
    chips.push({
      key: 'providerStatus',
      label: 'Provider status',
      value: vals.providerStatus,
    });
  }
  if (vals.paymentMethod) {
    chips.push({
      key: 'paymentMethod',
      label: 'Method',
      value: vals.paymentMethod,
    });
  }
  if (vals.provider) {
    chips.push({key: 'provider', label: 'Provider', value: vals.provider});
  }
  if (vals.automated) {
    chips.push({key: 'automated', label: 'Automated', value: 'Yes'});
  }
  if (vals.startDate)
    chips.push({
      key: 'startDate',
      label: 'From',
      value: fmtDate(vals.startDate),
    });
  if (vals.endDate)
    chips.push({key: 'endDate', label: 'To', value: fmtDate(vals.endDate)});
  if (vals.minAmount !== undefined && vals.minAmount !== null && vals.minAmount !== '')
    chips.push({
      key: 'minAmount',
      label: 'Min',
      value: Number(vals.minAmount).toLocaleString(),
    });
  if (vals.maxAmount !== undefined && vals.maxAmount !== null && vals.maxAmount !== '')
    chips.push({
      key: 'maxAmount',
      label: 'Max',
      value: Number(vals.maxAmount).toLocaleString(),
    });
  return chips;
};

const WalletTransaction = () => {
  const [form] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [bulkUpdateForm] = Form.useForm();
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [bankDetails, setBankDetails] = useState({});
  const [openBankModal, setOpenBankModal] = useState(false);
  const {currency, provider, type} = useParams();
  const location = useLocation();
  const setActionDescriptor = useHistoryContextStore(
    state => state.setActionDescriptor,
  );
  const clearActionDescriptor = useHistoryContextStore(
    state => state.clearActionDescriptor,
  );
  const setPageDescriptor = useHistoryContextStore(
    state => state.setPageDescriptor,
  );
  const clearPageDescriptor = useHistoryContextStore(
    state => state.clearPageDescriptor,
  );
  const isSuperAdmin = useIsSuperAdmin();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [openTxModal, setOpenTxModal] = useState({isOpen: false});
  const [openEmailModal, setOpenEmailModal] = useState({isOpen: false});
  const [openBulkAutomateTransferModal, setOpenBulkAutomateTransferModal] =
    useState({isOpen: false});
  const [updateModal, setUpdateModal] = useState({
    isOpen: false,
    content: null,
  });
  const [viewFilesModal, setViewFilesModal] = useState({
    isOpen: false,
    files: [],
  });
  const [automateTransferModal, setAutomateTransferModal] = useState({
    isOpen: false,
    content: null,
    type: null,
  });
  const [bulkUpdateModal, setBulkUpdateModal] = useState({
    isOpen: false,
    content: null,
  });
  const [copied, setCopied] = useState(false);
  const searchValue = Form.useWatch('search', form);
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const activeStatus = Form.useWatch('status', form);
  const paymentMethod = Form.useWatch('paymentMethod', form);
  const filterType = Form.useWatch('type', form);
  const filterProvider = Form.useWatch('provider', form);
  const filterAutomated = Form.useWatch('automated', form);
  const filterProviderStatus = Form.useWatch('providerStatus', form);
  const filterMinAmount = Form.useWatch('minAmount', form);
  const filterMaxAmount = Form.useWatch('maxAmount', form);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsKeys, setSelectedRowsKeys] = useState([]);

  // While a transaction modal is open, tell the global "Add to history" button
  // to capture the open modal (as a re-triggerable action) instead of just the
  // page. Cleared when the modal closes or the page unmounts.
  useEffect(() => {
    // Only super admins can contribute actions/pages to shortcuts.
    if (!isSuperAdmin) return undefined;
    if (updateModal.isOpen && updateModal.content) {
      const tx = updateModal.content;
      setActionDescriptor({
        path: location.pathname,
        title: `Transaction ${tx.referenceId || tx.id || ''}`.trim(),
        subtitle: tx.currency ? `${tx.currency} • Wallet` : 'Wallet',
        context: {scope: 'wallet-transaction', currency: tx.currency},
        action: {
          type: 'wallet-transaction',
          id: tx.id,
          currency: tx.currency,
          referenceId: tx.referenceId,
          userId: tx?.user?.id || null,
        },
      });
    } else {
      clearActionDescriptor();
    }
    return () => clearActionDescriptor();
  }, [
    isSuperAdmin,
    updateModal.isOpen,
    updateModal.content,
    location.pathname,
    setActionDescriptor,
    clearActionDescriptor,
  ]);

  const handleCopy = referenceId => {
    navigator.clipboard.writeText(referenceId).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 400);
    });
  };

  const handleDownloadReceipt = async record => {
    try {
      await downloadTransactionReceipt(record);
      toast.success('Transaction receipt downloaded');
    } catch (error) {
      toast.error('Unable to generate the transaction receipt');
    }
  };

  const handleDownloadProviderSafeReceipt = async record => {
    try {
      await downloadProviderSafeTransactionReceipt(record);
      toast.success('Provider-safe transaction receipt downloaded');
    } catch (error) {
      toast.error('Unable to generate the provider-safe transaction receipt');
    }
  };

  const navigate = useNavigate();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Wallet),
  );

  const {canSendNotification, openSendNotification, sendNotificationModal} =
    useSendUserNotification();

  // Bumped after anything that can move a transaction between statuses: the
  // filters are unchanged then, so the summary would not refetch on its own.
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const refreshSummary = () => setSummaryRefresh(count => count + 1);
  const refetchAll = (...args) => {
    refreshSummary();
    return refetch(...args);
  };
  const refetchCurrentPage = () => {
    refreshSummary();
    handleFilter('', currentPage, limit);
  };

  // The summary cards double as status tabs, as on the swap page. The status
  // goes into the filter form too, so the panel, its chips and every later
  // page turn agree with the card that is lit.
  const selectStatus = status => {
    form.setFieldsValue({status});
    handleFilter({...form.getFieldsValue(), status}, 1, limit);
  };

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    handleFilter('', page, pageSize);
  };

  // `variables` follows every refetch(newInput), so the summary cards read the
  // list's current filters from here instead of restating them.
  const {
    data,
    loading,
    refetch,
    variables: listVariables,
  } = useQuery(GET_ALL_WALLET_TRANSACTIONS_FOR_ADMIN, {
    variables: {
      input: {
        currency,
        filters: {
          ...(provider && {provider}),
          ...(type && {type}),
        },
        pagination: {
          limit: 10,
          page: 1,
        },
      },
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onCompleted: data =>
      setTotalPages(data?.getAllTransactionsForAdmin?.totalDocs),
    onError: error => toast.error(error.message),
  });

  const [exportWithdrawals] = useLazyQuery(
    GET_ALL_WALLET_TRANSACTIONS_FOR_ADMIN,
    {
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: false,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'no-cache',
      context: {
        fetchOptions: {
          headers: {
            'X-Export-Request': 'true',
          },
        },
      },
      onError: error => {
        toast.error(error.message);
      },
    },
  );

  const handleFilter = (values, page, limit) => {
    const filterObj = values || {};
    if (startDate && startDate !== 'Invalid date') {
      filterObj.startDate = startDate;
    }

    if (endDate && endDate !== 'Invalid date') {
      filterObj.endDate = endDate;
    }

    // Numeric range on totalTransactionAmountIncludingFees. Pass through
    // only when a value is actually set — undefined/null/'' means "no
    // bound", not "0" (the server treats missing as unbounded).
    if (
      filterMinAmount !== undefined &&
      filterMinAmount !== null &&
      filterMinAmount !== ''
    ) {
      filterObj.minAmount = Number(filterMinAmount);
    }
    if (
      filterMaxAmount !== undefined &&
      filterMaxAmount !== null &&
      filterMaxAmount !== ''
    ) {
      filterObj.maxAmount = Number(filterMaxAmount);
    }

    if (values) {
      setCurrentPage(1);
      setLimit(10);
    }

    try {
      refetch({
        input: {
          currency,
          filters: {
            ...(values?.locked && {locked: values?.locked}),
            ...(type ? {type} : {type: filterType}),
            ...(activeStatus ? {status: activeStatus} : {}),
            ...(paymentMethod ? {paymentMethod} : {}),
            ...(provider ? {provider} : {provider: filterProvider}),
            ...(filterAutomated ? {automated: true} : {}),
            ...(filterProviderStatus
              ? {providerStatus: filterProviderStatus}
              : {}),
            ...filterObj,
            ...(searchValue && {search: searchValue}),
          },
          pagination: {
            page: values ? 1 : page,
            limit: values ? 10 : limit,
          },
        },
      });
      if (values?.export) {
        toast.success('Exporting transactions to email');
        form.setFieldsValue({
          export: false,
          adminEmail: undefined,
        });
      }
      setOpenFilterModal(false);
    } catch (error) {
      console.log(error);
    }
  };

  // The filters currently applied via the FILTER form (watched form values).
  const activeTxFilters = {
    type: filterType || undefined,
    status: activeStatus || undefined,
    paymentMethod: paymentMethod || undefined,
    provider: filterProvider || undefined,
    search: searchValue || undefined,
    automated: filterAutomated || undefined,
    providerStatus: filterProviderStatus || undefined,
    startDate:
      startDate && startDate !== 'Invalid date' ? startDate : undefined,
    endDate: endDate && endDate !== 'Invalid date' ? endDate : undefined,
    minAmount:
      filterMinAmount !== undefined &&
      filterMinAmount !== null &&
      filterMinAmount !== ''
        ? Number(filterMinAmount)
        : undefined,
    maxAmount:
      filterMaxAmount !== undefined &&
      filterMaxAmount !== null &&
      filterMaxAmount !== ''
        ? Number(filterMaxAmount)
        : undefined,
  };

  // Expose the currency + applied filters to the floating "Add to shortcuts"
  // button so a saved snapshot resumes this exact filtered view.
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const summary = summarizeWalletTxFilters(activeTxFilters);
    setPageDescriptor({
      path: location.pathname,
      title: `${currency || 'Wallet'} Transactions`.trim(),
      subtitle: 'Wallet',
      context: {scope: 'page', tabKey: 'wallet-transactions', currency},
      filters: summary.length
        ? {count: summary.length, summary, data: activeTxFilters}
        : null,
    });
    return () => clearPageDescriptor();
  }, [
    isSuperAdmin,
    searchValue,
    startDate,
    endDate,
    activeStatus,
    paymentMethod,
    filterType,
    filterProvider,
    filterAutomated,
    filterMinAmount,
    filterMaxAmount,
    currency,
    location.pathname,
    setPageDescriptor,
    clearPageDescriptor,
  ]);

  // Apply filters restored from a shortcut: fill the form and refetch directly
  // (not via the watched-value handler, which would be a tick stale).
  const applyIncomingTxFilters = data => {
    if (!data) return;
    form.setFieldsValue({
      search: data.search,
      type: data.type,
      status: data.status,
      paymentMethod: data.paymentMethod,
      provider: data.provider,
      automated: data.automated,
      providerStatus: data.providerStatus,
      startDate: data.startDate,
      endDate: data.endDate,
      minAmount: data.minAmount,
      maxAmount: data.maxAmount,
    });
    setCurrentPage(1);
    refetch({
      input: {
        currency,
        filters: {
          ...(type ? {type} : data.type ? {type: data.type} : {}),
          ...(provider
            ? {provider}
            : data.provider
            ? {provider: data.provider}
            : {}),
          ...(data.status && {status: data.status}),
          ...(data.paymentMethod && {paymentMethod: data.paymentMethod}),
          ...(data.search && {search: data.search}),
          ...(data.startDate &&
            data.startDate !== 'Invalid date' && {startDate: data.startDate}),
          ...(data.endDate &&
            data.endDate !== 'Invalid date' && {endDate: data.endDate}),
          ...(data.automated ? {automated: true} : {}),
          ...(data.providerStatus && {providerStatus: data.providerStatus}),
          ...(data.minAmount !== undefined &&
            data.minAmount !== null &&
            data.minAmount !== '' && {minAmount: Number(data.minAmount)}),
          ...(data.maxAmount !== undefined &&
            data.maxAmount !== null &&
            data.maxAmount !== '' && {maxAmount: Number(data.maxAmount)}),
        },
        pagination: {page: 1, limit: 10},
      },
    });
  };

  // A restored shortcut can carry an action (re-open a transaction modal)
  // and/or filters (re-apply them).
  useEffect(() => {
    const action = location.state?.historyAction;
    const hf = location.state?.historyFilters;
    if (action?.type === 'wallet-transaction' && action.id) {
      setUpdateModal({
        isOpen: true,
        content: {
          id: action.id,
          currency: action.currency,
          referenceId: action.referenceId,
          user: action.userId ? {id: action.userId} : undefined,
        },
      });
    }
    if (hf?.data) {
      applyIncomingTxFilters(hf.data);
    }
    if (action || hf) window.history.replaceState({}, document.title);
  }, [location.key]);

  // Function to reset export fields to default state
  const handleCloseFilterModal = () => {
    form.setFieldsValue({
      export: false,
      adminEmail: undefined,
    });
    setOpenFilterModal(false);
  };

  const handleExport = values => {
    const filterObj = {...values};
    // Remove export and adminEmail from filterObj as they're handled separately
    // eslint-disable-next-line no-unused-vars
    const {export: _, adminEmail, ...filters} = filterObj;

    // Build filters similar to handleFilter but without pagination
    const exportFilters = {
      ...(filters?.locked && {locked: filters?.locked}),
      ...(type ? {type} : {type: filters?.type || filterType}),
      ...(filters?.status || activeStatus
        ? {status: filters?.status || activeStatus}
        : {}),
      ...(filters?.paymentMethod || paymentMethod
        ? {paymentMethod: filters?.paymentMethod || paymentMethod}
        : {}),
      ...(provider
        ? {provider}
        : {provider: filters?.provider || filterProvider}),
      ...(filters?.direction && {direction: filters.direction}),
      ...(filters?.startDate && {startDate: filters.startDate}),
      ...(filters?.endDate && {endDate: filters.endDate}),
      ...(filters?.search || searchValue
        ? {search: filters?.search || searchValue}
        : {}),
      ...(filters?.automated || filterAutomated ? {automated: true} : {}),
      ...(filters?.providerStatus || filterProviderStatus
        ? {providerStatus: filters?.providerStatus || filterProviderStatus}
        : {}),
      ...((filters?.minAmount ?? filterMinAmount) !== undefined &&
        (filters?.minAmount ?? filterMinAmount) !== null &&
        (filters?.minAmount ?? filterMinAmount) !== '' && {
          minAmount: Number(filters?.minAmount ?? filterMinAmount),
        }),
      ...((filters?.maxAmount ?? filterMaxAmount) !== undefined &&
        (filters?.maxAmount ?? filterMaxAmount) !== null &&
        (filters?.maxAmount ?? filterMaxAmount) !== '' && {
          maxAmount: Number(filters?.maxAmount ?? filterMaxAmount),
        }),
      type: 'WITHDRAWAL',
      export: true,
      adminEmail: adminEmail,
    };

    exportWithdrawals({
      variables: {
        input: {
          currency,
          filters: exportFilters,
        },
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: false,
    })
      .then(({data: {getAllTransactionsForAdmin}}) => {
        if (getAllTransactionsForAdmin.__typename === 'Error') {
          toast.error(getAllTransactionsForAdmin.message);
        } else {
          toast.success('Exported Successfully');
          form.setFieldsValue({
            export: false,
            adminEmail: undefined,
          });
          setOpenFilterModal(false);
        }
      })
      .catch(err => {
        console.log(err);
        toast.error('Export failed');
      });
  };

  const handleOpenModal = (data, event) => {
    if (event.target.isConnected) {
      setOpenBankModal(true);
      setBankDetails(data);
    }
  };

  // Only a pending withdrawal can be bulk-automated. On a page of deposits
  // that meant a whole column of checkboxes nobody could tick, so the column
  // only appears when at least one row on the page is actually selectable —
  // and only for an admin who could act on it.
  const transactions = data?.getAllTransactionsForAdmin?.transactions ?? [];
  const isAutomatable = record =>
    record?.type === 'WITHDRAWAL' && record?.status === 'PENDING';
  const showSelection =
    !!tabOperations?.update && transactions.some(isAutomatable);

  const rowSelection = {
    selectedRowKeys: selectedRowsKeys,
    // The checkbox is deliberately not pinned. antd never resolved a sticky
    // offset for the column after it, so Name inherited left:0 and rode on
    // top of the checkbox once the row scrolled. Name alone pins cleanly.
    columnWidth: 48,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRows(selectedRows);
      setSelectedRowsKeys(selectedRowKeys);
    },
    getCheckboxProps: record => ({
      disabled: !isAutomatable(record),
      // On a mixed page the disabled ones still need to say why.
      title: isAutomatable(record)
        ? undefined
        : 'Only a pending withdrawal can be automated',
    }),
  };

  const column = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // Who the transaction belongs to is the one column that has to stay
      // readable while the row scrolls; antd only pins a left-fixed column
      // when it is contiguous from the left, so it leads.
      fixed: 'left',
      width: 190,
      render: (text, record) => {
        const name = `${record?.user?.firstName ?? ''} ${
          record?.user?.lastName ?? ''
        }`.trim();
        const userId = record?.user?.id;
        if (userId) {
          return (
            <a
              href={`/dashboard/user/${userId}/wallet-settings`}
              target="_blank"
              rel="noreferrer"
              className={styles.userNameLink}
              onClick={e => e.stopPropagation()}>
              {name || '-'}
            </a>
          );
        }
        return <p>{name || '-'}</p>;
      },
    },

    {
      title: 'Reference ID',
      dataIndex: 'referenceId',
      key: 'referenceId',
      render: (text, record) => (
        <Tooltip
          trigger="hover"
          title={copied ? 'Copied' : record?.referenceId}>
          <p
            onClick={e => {
              e.stopPropagation();
              handleCopy(record?.referenceId);
            }}
            className={styles.reference}>
            {renderTransactionId(record?.referenceId)}
          </p>
        </Tooltip>
      ),
    },
    {
      title: 'Provider ID',
      dataIndex: 'providerId',
      key: 'providerId',
      // For guarded rails (WISE / REVOLUT / INTERAC / wire / bank) the tx
      // usually doesn't carry a paymentGateway.metadata.transactionId, but
      // the admin-stored providerTransferId is what the ops team wants to
      // see and paste into the provider's dashboard. Prefer that when
      // present, fall back to the automation-read Wise value, then the old
      // metadata.transactionId for everything else.
      render: (text, record) => {
        const providerId =
          record?.paymentGateway?.metadata?.providerTransferId ||
          record?.paymentGateway?.metadata?.wiseExtractedTransferId ||
          record?.paymentGateway?.metadata?.transactionId;
        // Settled by the customer's own Wise transfer id, no receipt review.
        const instant =
          record?.paymentGateway?.metadata?.providerTransferIdBy ===
          'USER_WISE_RESOLUTION';
        const senderUnverified =
          instant &&
          record?.paymentGateway?.metadata?.wiseResolution?.senderVerified ===
            false;
        return (
          <>
            <Tooltip trigger="hover" title={copied ? 'Copied' : providerId}>
              <p
                onClick={e => {
                  e.stopPropagation();
                  handleCopy(providerId);
                }}
                className={styles.reference}>
                {renderTransactionId(providerId)}
              </p>
            </Tooltip>
            {instant && <Tag color="blue">Instant</Tag>}
            {senderUnverified && (
              <Tooltip
                title={`Wise sender: ${
                  record?.paymentGateway?.metadata?.wiseResolution
                    ?.senderName || 'unknown'
                }`}>
                <Tag color="orange">Sender unverified</Tag>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      // This column used to be headed "Type" and render `direction`, so a swap
      // read as "WITHDRAWAL" with no hint of the currency pair.
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => (
        <WalletTxTypeCell
          record={record}
          onOpenSwap={leg =>
            navigate(
              `/dashboard/wallet/swap-transactions/${leg.from}?target=${leg.to}`,
            )
          }
        />
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      render: (text, record) => (
        <span>
          {record?.paymentProvider
            ?.toLowerCase()
            ?.replace(/\b\w/g, c => c.toUpperCase())}
        </span>
      ),
    },
    {
      title: 'Payment Method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (text, record) => (
        <div
          onClick={e => {
            e.stopPropagation();
            handleOpenModal(
              {
                ...record?.paymentMetadata,
                email: record?.paymentGateway?.metadata?.email,
                paymentProvider: record?.paymentProvider,
              },
              e,
            );
          }}
          style={{width: 170}}>
          <span>
            {record?.paymentMetadata?.paymentMethod ? (
              <Tag
                color={
                  {
                    'WIRE_TRANSFER': 'gold',
                    'BANK_TRANSFER': 'geekblue',
                    'CRYPTO': 'orange',
                    'THIRD_PARTY_FLUTTERWAVE': 'purple',
                    'THIRD_PARTY_PAYSTACK': 'magenta',
                    'THIRD_PARTY_PAYPAL': 'cyan',
                    'THIRD_PARTY_SVB': 'volcano',
                    'THIRD_PARTY_REVOLUT': 'green',
                    'THIRD_PARTY_WISE': 'lime',
                    'MOBILE_MONEY': 'blue',
                    'HUB2_MOBILE_MONEY': 'geekblue',
                    'MAVIANCE_MOBILE_MONEY': 'orange',
                  }[record?.paymentMetadata?.paymentMethod] || 'default'
                }>
                {record?.paymentMetadata?.paymentMethod
                  .replace(/_/g, ' ')
                  .toLowerCase()
                  .replace(/\b\w/g, c => c.toUpperCase())}
              </Tag>
            ) : (
              '-- -- --'
            )}
          </span>
          <SelectOutlined />
        </div>
      ),
    },
    {
      title: 'Total Amount',
      dataIndex: 'amountNetOfFees',
      key: 'amountNetOfFees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(
            record?.totalTransactionAmountIncludingFees || 0,
          )?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Amount (Net of Fees)',
      dataIndex: 'amountNetOfFees',
      key: 'amountNetOfFees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionAmountNetOfFees || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Fees',
      dataIndex: 'fees',
      key: 'fees',
      render: (text, record) => (
        <span>
          {currencySymbols[record?.currency]?.symbol}
          {Number(record?.transactionFee || 0)?.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Locked Transaction',
      dataIndex: 'automated',
      key: 'automated',
      render: (text, render) => (
        <Tag color={render?.locked ? 'green' : 'red'}>
          {render?.locked ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        // A stored failure reason (code + EN/FR) surfaces via a tooltip on
        // the tag itself so admins don't have to open the update modal to
        // see why a tx failed. Reads directly off the queried fields — no
        // extra request. Falls back to just the status tag when no reason
        // is stored.
        const reasonCode = record?.failureReasonCode;
        const reasonEN = record?.failureReasonMessages?.EN;
        const reasonFR = record?.failureReasonMessages?.FR;
        const hasReason = reasonCode || reasonEN || reasonFR;
        const tag = (
          <Tag
            color={
              {
                'SUCCESSFUL': 'green',
                'PENDING': 'yellow',
                'FAILED': 'red',
              }[record?.status] || 'blue'
            }
            key={record?.enabled}>
            {record?.status}
          </Tag>
        );
        if (!hasReason) return tag;
        return (
          <Tooltip
            trigger="hover"
            overlayStyle={{maxWidth: 360}}
            title={
              <span style={{whiteSpace: 'pre-line'}}>
                {reasonCode ? `Code: ${reasonCode}\n` : ''}
                {reasonEN ? `EN: ${reasonEN}` : ''}
                {reasonEN && reasonFR ? '\n' : ''}
                {reasonFR ? `FR: ${reasonFR}` : ''}
              </span>
            }>
            {tag}
          </Tooltip>
        );
      },
    },
    {
      // Provider-side state, separate from the Daba status above: a PENDING
      // withdrawal reads NOT_SENT until the automata hands it to the provider,
      // then SUBMITTED, so an admin working the queue sees each row move the
      // moment it is sent rather than when the poll settles it.
      title: 'Provider Status',
      dataIndex: 'providerStatus',
      key: 'providerStatus',
      render: (text, record) => (
        <ProviderStatusTag
          status={record?.providerStatus}
          transferId={record?.providerTransferId}
        />
      ),
    },
    {
      title: 'Date & Time',
      dataIndex: 'time',
      key: 'time',
      render: (text, record) => (
        <p className={styles.reference}>
          {moment(record?.createdAt).format('M/D/YY hh:mm a')}
        </p>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      width: 80,
      key: 'action',
      render: (text, record) => {
        const menuItems = [
          {
            key: 'download-receipt',
            label: 'Download receipt',
            onClick: async e => {
              e.domEvent.stopPropagation();
              await handleDownloadReceipt(record);
            },
          },
        ];

        if (supportsProviderSafeReceipt(record)) {
          menuItems.splice(1, 0, {
            key: 'download-provider-safe-receipt',
            label: 'Download provider-safe receipt',
            onClick: async e => {
              e.domEvent.stopPropagation();
              await handleDownloadProviderSafeReceipt(record);
            },
          });
        }

        // Only add these items if the record type is WITHDRAWAL
        if (record?.type === 'WITHDRAWAL') {
          menuItems.push(
            {
              key: '1',
              label: 'Automate Transfer',
              onClick: e => {
                e.domEvent.stopPropagation();
                setAutomateTransferModal({
                  isOpen: true,
                  content: record,
                  type: 'TRANSFER',
                });
              },
            },
            {
              key: '2',
              label: 'Transfer Status',
              onClick: e => {
                e.domEvent.stopPropagation();
                setAutomateTransferModal({
                  isOpen: true,
                  content: record,
                  type: 'TRANSFER_STATUS',
                });
              },
            },
          );
        }

        const files = record?.paymentGateway?.metadata?.files;
        if (files && files.length > 0) {
          menuItems.push({
            key: '3',
            label: 'View Files',
            onClick: e => {
              e.domEvent.stopPropagation();
              setViewFilesModal({
                isOpen: true,
                files: files,
              });
            },
          });
        }

        const notifyItem = buildSendNotificationMenuItem({
          user: record?.user,
          context: {
            label: `${record?.direction ?? 'Wallet'} transaction`,
            reference: renderTransactionId(record?.referenceId),
          },
          onOpen: openSendNotification,
          allowed: canSendNotification,
        });
        if (notifyItem) menuItems.push(notifyItem);

        return (
          <Dropdown
            trigger={'click'}
            menu={{
              items: menuItems,
            }}>
            <img src={moreIcon} alt="" onClick={e => e.stopPropagation()} />
          </Dropdown>
        );
      },
    },
  ];

  // Shared by the pager above the table and the one below it, so the two can
  // never drift out of step.
  const pagerProps = {
    showQuickJumper: true,
    showSizeChanger: true,
    current: currentPage,
    total: totalPages,
    pageSize: limit,
    onChange: handlePageChange,
  };

  const activeFilterChips = summarizeWalletTxFilters(form.getFieldsValue());

  return (
    <div className={shell.page}>
      <div className={shell.header}>
        <div className={shell.titleWrap}>
          <button
            type="button"
            className={shell.backButton}
            onClick={() => navigate('/dashboard/wallet')}>
            <ArrowLeftOutlined /> Wallet
          </button>
          <h1 className={shell.title}>
            {[provider, type, currency].filter(Boolean).join(' · ')}{' '}
            transactions
          </h1>
          <p className={shell.subtitle}>
            Deposits and withdrawals moving through this wallet. Open a row to
            review or correct one.
          </p>
        </div>

        <div className={shell.headerActions}>
          <AntButton
            icon={<ReloadOutlined />}
            onClick={() => refetchAll()}
            loading={loading}>
            Refresh
          </AntButton>
          <AntButton
            icon={<FilterOutlined />}
            onClick={() => setOpenFilterModal(!openFilterModal)}>
            Filter
            {activeFilterChips.length ? ` (${activeFilterChips.length})` : ''}
          </AntButton>
          {selectedRows.length > 0 && tabOperations?.update && (
            <AntButton
              onClick={() => setOpenBulkAutomateTransferModal({isOpen: true})}>
              Automate {selectedRows.length} transfer
              {selectedRows.length === 1 ? '' : 's'}
            </AntButton>
          )}
          <Dropdown
            trigger="click"
            menu={{
              items: [
                {
                  key: 1,
                  label: 'Create Manual Deposit',
                  onClick: () =>
                    setOpenTxModal({isOpen: true, type: 'DEPOSIT'}),
                },
                {
                  key: 2,
                  label: 'Create Manual Withdrawal',
                  onClick: () =>
                    setOpenTxModal({isOpen: true, type: 'WITHDRAWAL'}),
                },
                {
                  key: 3,
                  label: 'Add Email(s) for Failure/Pending TX',
                  onClick: () => setOpenEmailModal({isOpen: true}),
                },
                {
                  key: 4,
                  label: 'Bulk Update Pending Transactions',
                  onClick: () => setBulkUpdateModal({isOpen: true}),
                },
              ],
            }}>
            <AntButton type="primary" className={shell.primaryButton}>
              Wallet actions
            </AntButton>
          </Dropdown>
        </div>
      </div>

      <TransactionSummaryCards
        listInput={listVariables?.input}
        refreshSignal={summaryRefresh}
        activeStatus={activeStatus}
        onSelect={selectStatus}
      />

      {/* The filter panel keeps its own markup; only where it sits changed. */}
      <div style={{display: openFilterModal ? 'block' : 'none'}}>
        <TransactionsFilter
          form={form}
          handleFilter={handleFilter}
          refetch={refetch}
          handleClose={handleCloseFilterModal}
          handleExport={handleExport}
        />
      </div>

      {activeFilterChips.length > 0 && (
        <div className={shell.filterMeta}>
          <span className={shell.resultCount}>
            {totalPages.toLocaleString('en-US')} matching
          </span>
          {activeFilterChips.map(chip => (
            <Tag key={chip.key}>
              {chip.label}: {chip.value}
            </Tag>
          ))}
        </div>
      )}

      {/* The same pager as the one below the table, pinned to the top so
          paging does not mean scrolling to the last row and back. Deliberately
          a sibling of the table container rather than a child: that container
          is overflow:hidden, which would trap the sticky positioning inside
          it. */}
      <TableTopScrollbar pagerProps={pagerProps} />
      <section
        className={`${shell.tableContainer} ${styles.scrollTable} ${
          showSelection ? styles.withSelection : ''
        }`}>
        <Table
          rowKey="id"
          rowSelection={
            showSelection ? {type: 'checkbox', ...rowSelection} : undefined
          }
          loading={loading}
          onRow={record => ({
            onClick: () => {
              setUpdateModal({isOpen: true, content: record});
            },
            className: styles.tableRow,
          })}
          columns={column}
          pagination={false}
          dataSource={data?.getAllTransactionsForAdmin?.transactions}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  activeFilterChips.length
                    ? 'Nothing matches these filters.'
                    : 'No transactions in this wallet yet.'
                }
              />
            ),
          }}
        />
        <Pagination {...pagerProps} className="ant-table-pagination" />
      </section>
      <DBModal
        isOpen={openBankModal}
        content={
          <WireTransferDetails isOpen={openBankModal} data={bankDetails} />
        }
        handleClose={() => setOpenBankModal(false)}
      />
      <ManualTxModal
        isOpen={openTxModal?.isOpen}
        onClose={() => setOpenTxModal(false)}
        type={openTxModal?.type}
        refetch={refetchAll}
      />
      <EmailTriggerModal
        isOpen={openEmailModal.isOpen}
        onCloseModal={() => setOpenEmailModal({isOpen: false})}
      />
      <DBModal
        isOpen={updateModal.isOpen}
        destroyOnClose={true}
        handleClose={() => {
          updateForm.resetFields();
          setUpdateModal({isOpen: false, content: null});
        }}
        width={900}
        content={
          <UpdateWalletTransaction
            currency={updateModal?.content?.currency}
            id={updateModal?.content?.id}
            operation={tabOperations?.update}
            form={updateForm}
            refetch={refetchCurrentPage}
            closeModal={() => {
              updateForm.resetFields();
              setUpdateModal({isOpen: false, content: null});
            }}
            userId={updateModal?.content?.user?.id}
          />
        }
      />
      <DBModal
        isOpen={automateTransferModal.isOpen}
        handleClose={() => {
          setAutomateTransferModal({isOpen: false, content: null, type: null});
        }}
        width={900}
        content={
          <AutomateTransferModal
            refetch={refetchCurrentPage}
            content={automateTransferModal.content}
            type={automateTransferModal.type}
            onClose={() => {
              setAutomateTransferModal({
                isOpen: false,
                content: null,
                type: null,
              });
            }}
          />
        }
      />
      <DBModal
        isOpen={bulkUpdateModal.isOpen}
        handleClose={() => {
          setBulkUpdateModal({isOpen: false, content: null});
          bulkUpdateForm.resetFields();
        }}
        content={
          <BulkUploadWithdrawals
            form={bulkUpdateForm}
            onClose={() => {
              setBulkUpdateModal({isOpen: false, content: null});
              bulkUpdateForm.resetFields();
            }}
            refetch={refetchCurrentPage}
          />
        }
      />
      <DBModal
        isOpen={openBulkAutomateTransferModal.isOpen}
        handleClose={() => {
          setOpenBulkAutomateTransferModal({isOpen: false, content: null});
        }}
        width={900}
        content={
          <BulkAutomateTransfer
            rows={selectedRows}
            setSelectedRows={() => {
              setSelectedRows([]);
              setSelectedRowsKeys([]);
            }}
            refetch={refetchAll}
            onClose={() => {
              setOpenBulkAutomateTransferModal({isOpen: false, content: null});
            }}
          />
        }
      />
      <ViewFilesModal
        isOpen={viewFilesModal.isOpen}
        onClose={() =>
          setViewFilesModal({
            isOpen: false,
            files: [],
          })
        }
        files={viewFilesModal.files}
      />
      {sendNotificationModal}
    </div>
  );
};

export default WalletTransaction;
